// utils/emailCompanyMatch.js

const FREE_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "aol.com",
  "protonmail.com",
  "mail.com",
  "live.com",
  "msn.com",
  "yandex.com",
];

function normalise(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .trim();
}

function getAbbreviation(companyName) {
  return companyName
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word) => word[0])
    .join("");
}

function getSignificantWords(companyName) {
  // Remove common business suffixes and get meaningful words
  const commonSuffixes = [
    "inc",
    "ltd",
    "llc",
    "corp",
    "corporation",
    "limited",
    "company",
    "co",
  ];
  return companyName
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 2 && !commonSuffixes.includes(word));
}

export function checkEmailBelongsToCompany(email, companyName) {
  if (!email || !companyName) {
    return {
      belongs: false,
      confidence: 0,
      reasons: ["Missing email or company name"],
    };
  }

  const emailLower = email.toLowerCase().trim();
  const [localPart, domain] = emailLower.split("@");

  if (!domain) {
    return {
      belongs: false,
      confidence: 0,
      reasons: ["Invalid email format"],
    };
  }

  // Free email check first - immediate rejection
  if (FREE_EMAIL_DOMAINS.includes(domain)) {
    return {
      belongs: false,
      confidence: 0,
      reasons: ["Email uses free email provider - company email required"],
    };
  }

  const normalisedCompany = normalise(companyName);
  const significantWords = getSignificantWords(companyName);
  const abbreviation = getAbbreviation(companyName);
  const domainWithoutTld = domain.split(".")[0]; // e.g., "riddle" from "riddle.com"

  let score = 0;
  const reasons = [];

  // 1. Exact company name match in domain (100% match)
  if (domainWithoutTld === normalisedCompany) {
    return {
      belongs: true,
      confidence: 100,
      reasons: ["Exact company name match in domain"],
    };
  }

  // 2. Domain contains full company name
  if (domainWithoutTld.includes(normalisedCompany)) {
    score += 100;
    reasons.push("Company name found in domain");
  }

  // 3. Exact abbreviation match in domain
  if (abbreviation.length >= 2 && domainWithoutTld === abbreviation) {
    return {
      belongs: true,
      confidence: 100,
      reasons: ["Company abbreviation matches domain exactly"],
    };
  }

  // 4. Domain contains abbreviation
  if (abbreviation.length >= 2 && domainWithoutTld.includes(abbreviation)) {
    score += 80;
    reasons.push("Company abbreviation found in domain");
  }

  // 5. Significant word matches in domain (each word is important)
  const domainWordMatches = significantWords.filter((word) =>
    domainWithoutTld.includes(normalise(word)),
  );
  if (domainWordMatches.length > 0) {
    // If at least one significant word matches, it's likely valid
    score += domainWordMatches.length * 50;
    reasons.push(
      `Company word(s) found in domain: ${domainWordMatches.join(", ")}`,
    );
  }

  // 6. Check local part (minimal weight)
  if (localPart.includes(normalisedCompany)) {
    score += 20;
    reasons.push("Company name found in email address");
  }

  const confidence = Math.max(0, Math.min(100, score));

  return {
    belongs: confidence >= 50,
    confidence,
    reasons,
  };
}
